/// <amd-module name="Repos/PushesList/Components/PushesTable" />
import * as React from "react";
import { GitPush } from "Repos/Common/Contracts/Git";
import { ICommit } from "Repos/Common/Contracts/History";
import { getShortCommitId } from "Repos/Common/Util/Ref";
import { PipelineStatus } from "Repos/PipelineStatus/Components/PipelineStatus";
import * as Resources from "Repos/PushesList/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { IScreenContext, ScreenBreakpoints, ScreenContext, ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { onClickFPS } from "VSS/Platform/FPS";
import { VssComponent } from "VSS/Platform/Layout";
import { Ago } from "VSSUI/Ago";
import { Link } from "VSSUI/Link";
import { ListSelection } from "VSSUI/List";
import { Observer } from "VSSUI/Observer";
import { IBasicIdentityRef, Persona, PersonaSize } from "VSSUI/Persona";
import {
    IMeasurementStyle,
    ITableBreakpoint,
    ITableColumn,
    ITableRow,
    SimpleTableCell,
    TableColumnLayout,
    TableLoadingRow,
    TwoLineTableCell
} from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ExpandableTreeCell, ITreeColumn, ITreeRowDetails, renderTreeRow, Tree, TreeRow } from "VSSUI/TreeEx";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { ITreeItem, ITreeItemEx, ITreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { IPush, IPushTableItem, ISectionTitle, PushTableItemType } from "../Contracts";

export interface IPushesTableProps {
    loadMore: (parent: ITreeItem<IPushTableItem | ObservableValue<IPushTableItem | undefined>> | undefined) => void;
    onToggle: (event: React.SyntheticEvent<HTMLElement>, treeItem: ITreeItemEx<IPushTableItem | ObservableValue<IPushTableItem | undefined>>) => void;
    pushes: ITreeItemProvider<IPushTableItem | ObservableValue<IPushTableItem | undefined>>;
}

const tableBreakpoints: ITableBreakpoint[] = [
    {
        breakpoint: ScreenBreakpoints.xsmall,
        columnWidths: [2.5, -100]
    },
    {
        breakpoint: ScreenBreakpoints.small,
        columnWidths: [5.25, -100]
    }
];

export class PushesTable extends VssComponent<IPushesTableProps> {
    private selection = new ListSelection({ multiSelect: false });
    private columns = [creatorColumn, titleColumn];

    public render() {
        return (
            <Tree<IPushTableItem | ObservableValue<IPushTableItem | undefined>>
                ariaLabel={Resources.PushesTable}
                className="repos-pushes-table"
                columns={this.columns}
                itemProvider={this.props.pushes}
                onActivate={this.onActivateExpand}
                onToggle={this.props.onToggle}
                renderLoadingRow={(rowIndex: number, rowDetails: ITreeRowDetails<IPushTableItem | ObservableValue<IPushTableItem | undefined>>) => {
                    let treeItem: ITreeItem<IPushTableItem | ObservableValue<IPushTableItem | undefined>> | undefined = undefined;
                    if (rowDetails.data.parentItem) {
                        treeItem = rowDetails.data.parentItem.underlyingItem;
                    }
                    this.props.loadMore(treeItem);
                    return <TableLoadingRow columns={this.columns} details={rowDetails} rowIndex={rowIndex} />;
                }}
                selectableText={true}
                selection={this.selection}
                renderRow={this.renderRow}
                showHeader={false}
                showLines={true}
                tableBreakpoints={tableBreakpoints}
            />
        );
    }

    private onActivateExpand = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<ITreeItemEx<IPushTableItem>>): void => {
        if (!event.defaultPrevented) {
            if (tableRow.data.underlyingItem.data.itemType === PushTableItemType.Push && tableRow.data.underlyingItem.childItems) {
                this.props.onToggle(event, tableRow.data);
                event.preventDefault();
            } else if (tableRow.data.underlyingItem.data.itemType === PushTableItemType.Commit) {
                const commit = tableRow.data.underlyingItem.data.underlyingItem as ICommit;
                onClickFPS(this.context.pageContext, commit.linkProps.href!, true, event);
            }
        }
    };

    private renderRow = (rowIndex: number, item: ITreeItemEx<IPushTableItem>, details: ITreeRowDetails<IPushTableItem>): JSX.Element => {
        const isCommit = item.underlyingItem.data.itemType === PushTableItemType.Commit;
        if (item.underlyingItem.data.itemType === PushTableItemType.Title) {
            this.selection.addUnselectable(rowIndex);
            const sectionTitle = item.underlyingItem.data.underlyingItem as ISectionTitle;
            details.singleClickActivation = false;
            details.excludeTabStop = true;
            return (
                <TreeRow
                    details={details}
                    index={rowIndex}
                    linkProps={(item.underlyingItem.data as any).linkProps}
                    className="bolt-list-row-no-hover"
                >
                    <SimpleTableCell columnIndex={0} key={0} colspan={2} className="title-s padding-top-16">
                        <Observer pushCount={sectionTitle.pushCount}>
                            {(props: { pushCount: number }) => {
                                const sectionLabel =
                                    props.pushCount === 1
                                        ? format(Resources.PushesSectionsingleUpdatesTitle, sectionTitle.text)
                                        : format(Resources.PushesSectionMultipleUpdatesTitle, sectionTitle.text, props.pushCount);
                                return <div>{sectionLabel}</div>;
                            }}
                        </Observer>
                    </SimpleTableCell>
                </TreeRow>
            );
        } else {
            details.excludeTabStop = false;
            return renderTreeRow(
                rowIndex,
                item,
                details,
                [creatorColumn, titleColumn],
                item.underlyingItem.data,
                isCommit ? "repos-commits-row" : undefined
            );
        }
    };
}

const creatorColumn: ITreeColumn<IPushTableItem> = {
    columnLayout: TableColumnLayout.none,
    id: "creator",
    renderCell: (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<ITreeItemEx<IPushTableItem>>,
        tableItem: ITreeItemEx<IPushTableItem>
    ) => {
        return tableItem.underlyingItem.data.itemType === PushTableItemType.Push ? (
            <ExpandableTreeCell
                className="margin-right-8"
                columnIndex={columnIndex}
                key="creatorColumn"
                treeColumn={tableColumn}
                treeItem={tableItem}
            >
                <Observer columnWidth={tableColumn.width}>
                    {(props: { columnWidth: number }) =>
                        props.columnWidth >= 5.25 && (
                            <Persona
                                identity={createPersona((tableItem.underlyingItem.data.underlyingItem as IPush).push.push)}
                                size={PersonaSize.size40}
                            />
                        )
                    }
                </Observer>
            </ExpandableTreeCell>
        ) : (
            <SimpleTableCell columnIndex={columnIndex} key="creatorColumn" tableColumn={tableColumn} />
        );
    },
    width: new ObservableValue(5.25),
    widthStyle: IMeasurementStyle.REM
};

const titleColumn: ITreeColumn<IPushTableItem> = {
    columnLayout: TableColumnLayout.twoLine,
    id: "title",
    renderCell: renderPushesDataColumn,
    width: -100
};

function renderPushesDataColumn(
    rowIndex: number,
    columnIndex: number,
    tableColumn: ITableColumn<ITreeItemEx<IPushTableItem>>,
    tableItem: ITreeItemEx<IPushTableItem>
): JSX.Element {
    const { linkProps, pushedBy, commitId, pushDate, pushId, push } = tableItem.underlyingItem.data.underlyingItem as IPush;
    let line1: JSX.Element;
    let line2: JSX.Element;

    if (pushId === undefined) {
        const commit = tableItem.underlyingItem.data.underlyingItem as ICommit;

        line1 = (
            <Tooltip overflowOnly={true}>
                <div className="bolt-table-inline-link-left-padding text-ellipsis body-l font-weight-semibold">{commit.comment}</div>
            </Tooltip>
        );

        line2 = (
            <div className="bolt-table-inline-link-left-padding flex-row flex-center body-s secondary-text rhythm-horizontal-16 scroll-hidden">
                <Tooltip overflowOnly={true}>
                    <div className="flex-row flex-center monospaced-xs">{commit.shortCommitId}</div>
                </Tooltip>
                <Tooltip overflowOnly={true}>
                    <span className="text-ellipsis">{commit.commit.author.name}</span>
                </Tooltip>
                <Ago date={commit.commit.author.date} format={AgoFormat.Extended} />
            </div>
        );
    } else {
        const status = (tableItem.underlyingItem.data.underlyingItem as IPush).push.status;

        line1 = (
            <Link
                className="bolt-table-inline-link bolt-table-link font-weight-semibold body-l text-ellipsis"
                excludeTabStop={true}
                href={linkProps.href}
                tooltipProps={{ overflowOnly: true }}
                removeUnderline={true}
            >
                {linkProps.text}
            </Link>
        );

        line2 = (
            <ScreenContext.Consumer>
                {(screenContext: IScreenContext) => (
                    <Observer screenSize={screenContext.size}>
                        {(props: { screenSize: ScreenSize }) => (
                            <>
                                <div className="bolt-table-inline-link-left-padding flex-row flex-center body-s secondary-text rhythm-horizontal-16 scroll-hidden">
                                    <Tooltip overflowOnly={true}>
                                        <div className="flex-row flex-center monospaced-xs">{getShortCommitId(commitId)}</div>
                                    </Tooltip>
                                    <Tooltip overflowOnly={true}>
                                        <span className="text-ellipsis">{pushedBy.displayName}</span>
                                    </Tooltip>
                                    <Ago date={pushDate} format={AgoFormat.Extended} />
                                    {push.isForcePush ? (
                                        <Tooltip overflowOnly={true}>
                                            <span className="text-ellipsis force-push-tooltip">{Resources.ForcePush}</span>
                                        </Tooltip>
                                    ) : null}
                                </div>
                                {props.screenSize > ScreenSize.xsmall && (
                                    <PipelineStatus className="bolt-table-link margin-left-8" statuses={status} />
                                )}
                            </>
                        )}
                    </Observer>
                )}
            </ScreenContext.Consumer>
        );
    }

    return (
        <TwoLineTableCell
            className="bolt-table-cell-content-with-inline-link"
            columnIndex={columnIndex}
            key="pushesColumn"
            line1={line1}
            line2={line2}
            tableColumn={tableColumn}
        />
    );
}

function createPersona(push: GitPush): IBasicIdentityRef {
    const { _links, imageUrl } = push.pushedBy;
    const personaIdentity: IBasicIdentityRef = { id: push.pushedBy.id, displayName: push.pushedBy.displayName };

    if (_links && _links.avatar && _links.avatar.href) {
        personaIdentity.imageUrl = _links.avatar.href;
    } else if (imageUrl) {
        personaIdentity.imageUrl = imageUrl;
    }

    return personaIdentity;
}
